// ─────────────────────────────────────────────────────────────────────────────
// Scheduling — day view across rooms with open slots + booking.
//
// Columns = rooms (incl. Telehealth, Group). Rows = time slots. Booked slots
// show the appointment; empty slots are clickable "open rooms" that open the
// booking form pre-filled with that room/time. Appointments carry a CPT so
// check-out can auto-generate a claim (see businessStore.setAppointmentStatus).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo } from "react";
import * as biz from "../businessStore";
import { inp, primaryBtn, ghostBtn, Field, Badge, Stat } from "../ui";

const SLOTS = [];
for (let h = 8; h <= 17; h++) { SLOTS.push(`${String(h).padStart(2, "0")}:00`); SLOTS.push(`${String(h).padStart(2, "0")}:30`); }

const STATUS_STYLE = {
  scheduled: { color: "#1d4ed8", bg: "#dbeafe", label: "Scheduled" },
  confirmed: { color: "#0e7490", bg: "#cffafe", label: "Confirmed" },
  checked_in: { color: "var(--ed-violet)", bg: "#ede9fe", label: "Checked in" },
  checked_out: { color: "#15803d", bg: "#dcfce7", label: "Checked out" },
  no_show: { color: "#b91c1c", bg: "#fee2e2", label: "No-show" },
  cancelled: { color: "var(--ed-muted)", bg: "var(--ed-line-2)", label: "Cancelled" },
};

export default function Scheduling({ patients, onChange }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [tick, setTick] = useState(0);
  const bump = () => { setTick((t) => t + 1); onChange?.(); };
  const [booking, setBooking] = useState(null); // { roomId, start }

  /* eslint-disable react-hooks/exhaustive-deps -- `tick` is the store cache-buster */
  const rooms = useMemo(() => biz.getRooms(), [tick]);
  const providers = useMemo(() => biz.getProviders(), [tick]);
  const payers = useMemo(() => biz.getPayers(), [tick]);
  const appts = useMemo(() => biz.getAppointments({ date }), [date, tick]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const apptAt = (roomId, slot) => appts.find((a) => a.roomId === roomId && a.start === slot && a.status !== "cancelled");

  const dayStats = useMemo(() => {
    const total = appts.filter((a) => a.status !== "cancelled").length;
    const noShow = appts.filter((a) => a.status === "no_show").length;
    const out = appts.filter((a) => a.status === "checked_out").length;
    const slotsCount = rooms.length * SLOTS.length;
    return { total, noShow, out, util: slotsCount ? Math.round((total / slotsCount) * 100) : 0 };
  }, [appts, rooms]);

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, color: "var(--ed-ink)" }}>Scheduling</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button style={ghostBtn} onClick={() => setDate(shift(date, -1))}>←</button>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...inp, width: "auto" }} />
          <button style={ghostBtn} onClick={() => setDate(shift(date, 1))}>→</button>
          <button style={primaryBtn} onClick={() => setBooking({ roomId: rooms[0]?.id, start: "09:00" })}>+ Book</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
        <Stat label="Appointments" value={dayStats.total} />
        <Stat label="Utilization" value={dayStats.util + "%"} color="var(--ed-brand-lift)" />
        <Stat label="Checked out" value={dayStats.out} color="#15803d" />
        <Stat label="No-shows" value={dayStats.noShow} color={dayStats.noShow ? "var(--ed-alert)" : "#16a34a"} />
      </div>

      {/* Rooms grid */}
      <div className="card" style={{ padding: 0, overflow: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: `70px repeat(${rooms.length}, minmax(150px, 1fr))`, minWidth: 70 + rooms.length * 150 }}>
          <div style={hCell}>Time</div>
          {rooms.map((r) => <div key={r.id} style={hCell}>{r.name}</div>)}
          {SLOTS.map((slot) => (
            <React.Fragment key={slot}>
              <div style={{ ...tCell, color: "var(--ed-muted-2)", fontWeight: 700 }}>{slot}</div>
              {rooms.map((room) => {
                const a = apptAt(room.id, slot);
                if (a) {
                  const ss = STATUS_STYLE[a.status] || STATUS_STYLE.scheduled;
                  const type = biz.APPT_TYPES.find((t) => t.id === a.typeId);
                  return (
                    <div key={room.id} style={{ ...tCell, padding: 4 }}>
                      <button onClick={() => setBooking({ ...a, edit: true })} style={{ width: "100%", textAlign: "left", border: "none", borderLeft: `3px solid ${type?.color || "#0891b2"}`, background: ss.bg, color: "var(--ed-ink)", borderRadius: 6, padding: "6px 8px", cursor: "pointer", fontFamily: "inherit" }}>
                        <div style={{ fontWeight: 700, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.patientName}</div>
                        <div style={{ fontSize: 11, color: "var(--ed-ink-3)" }}>{a.typeLabel}</div>
                        <div style={{ fontSize: 10, color: ss.color, fontWeight: 700 }}>{ss.label}</div>
                      </button>
                    </div>
                  );
                }
                return (
                  <div key={room.id} style={{ ...tCell, padding: 4 }}>
                    <button onClick={() => setBooking({ roomId: room.id, start: slot })} title="Open — click to book"
                      style={{ width: "100%", height: "100%", minHeight: 30, border: "1px dashed var(--ed-line)", background: "transparent", borderRadius: 6, cursor: "pointer", color: "var(--ed-line-hover)", fontSize: 16 }}>+</button>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {booking && (
        <BookingModal booking={booking} date={date} rooms={rooms} providers={providers} payers={payers} patients={patients}
          onClose={() => setBooking(null)} onSaved={() => { setBooking(null); bump(); }} />
      )}
    </div>
  );
}

function BookingModal({ booking, date, rooms, providers, payers, patients, onClose, onSaved }) {
  const editing = booking.edit;
  const [f, setF] = useState(() => ({
    patientId: booking.patientId || patients[0]?.id || "",
    providerId: booking.providerId || providers[0]?.id || "",
    roomId: booking.roomId || rooms[0]?.id || "",
    typeId: booking.typeId || "individual",
    start: booking.start || "09:00",
    payerId: booking.payerId || "",
    status: booking.status || "scheduled",
  }));
  const type = biz.APPT_TYPES.find((t) => t.id === f.typeId);
  const cptCode = type?.cpt;
  const cptInfo = biz.cpt(cptCode);

  function save() {
    const patient = patients.find((p) => p.id === f.patientId);
    const end = addMinutes(f.start, cptInfo?.minutes || 50);
    biz.saveAppointment({
      id: editing ? booking.id : undefined,
      patientId: f.patientId, patientName: patient ? `${patient.firstName} ${patient.lastName}` : "—",
      providerId: f.providerId, roomId: f.roomId, date, start: f.start, end,
      typeId: f.typeId, typeLabel: type?.label, cptCode, payerId: f.payerId, status: f.status,
    });
    onSaved();
  }
  function status(s) { biz.setAppointmentStatus(booking.id, s); onSaved(); }

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 480, maxWidth: "92vw", padding: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: "var(--ed-ink)" }}>{editing ? "Appointment" : "Book appointment"}</div>
        <Field label="Patient"><select style={inp} value={f.patientId} onChange={(e) => setF({ ...f, patientId: e.target.value })}>{patients.map((p) => <option key={p.id} value={p.id}>{p.lastName}, {p.firstName}</option>)}</select></Field>
        <div style={{ display: "flex", gap: 10 }}>
          <Field label="Type" style={{ flex: 1 }}><select style={inp} value={f.typeId} onChange={(e) => setF({ ...f, typeId: e.target.value })}>{biz.APPT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select></Field>
          <Field label="Start" style={{ flex: 1 }}><select style={inp} value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })}>{SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Field label="Provider" style={{ flex: 1 }}><select style={inp} value={f.providerId} onChange={(e) => setF({ ...f, providerId: e.target.value })}>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
          <Field label="Room" style={{ flex: 1 }}><select style={inp} value={f.roomId} onChange={(e) => setF({ ...f, roomId: e.target.value })}>{rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></Field>
        </div>
        <Field label="Payer"><select style={inp} value={f.payerId} onChange={(e) => setF({ ...f, payerId: e.target.value })}><option value="">— Self-pay —</option>{payers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
        <div style={{ marginTop: 10, fontSize: 12.5, color: "#0e7490", background: "#ecfeff", borderRadius: 8, padding: "8px 10px" }}>
          Billing: <strong>{cptCode}</strong> — {cptInfo?.desc} · ${cptInfo?.fee} · {cptInfo?.minutes} min
        </div>

        {editing && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ed-ink-3)", marginBottom: 6 }}>Update status {booking.claimId && <Badge text="claim created" color="#15803d" bg="#dcfce7" />}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["confirmed", "checked_in", "checked_out", "no_show", "cancelled"].map((s) => (
                <button key={s} onClick={() => status(s)} style={{ ...ghostBtn, padding: "6px 11px", fontSize: 12 }}>{STATUS_STYLE[s].label}</button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "var(--ed-muted-2)", marginTop: 6 }}>Checking out a visit with a CPT auto-creates a claim in Billing.</div>
          </div>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={ghostBtn}>Close</button>
          <button onClick={save} style={primaryBtn}>{editing ? "Save changes" : "Book"}</button>
        </div>
      </div>
    </div>
  );
}

function shift(date, days) { const d = new Date(date); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); }
function addMinutes(hhmm, mins) { const [h, m] = hhmm.split(":").map(Number); const t = h * 60 + m + (mins || 0); return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; }

const hCell = { padding: "10px 8px", fontSize: 12, fontWeight: 800, color: "var(--ed-ink-3)", background: "var(--ed-bg)", borderBottom: "1px solid var(--ed-line)", position: "sticky", top: 0, textAlign: "center" };
const tCell = { borderBottom: "1px solid var(--ed-line-2)", borderLeft: "1px solid var(--ed-bg)", minHeight: 38, display: "flex", alignItems: "stretch", fontSize: 12, padding: "6px 8px" };
const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 };
